/**
 * Copyright © 2026 Adnuntius AS.
 */
import {BsDatepickerConfig, BsLocaleService} from 'ngx-bootstrap/datepicker';
import {PopoverDirective} from 'ngx-bootstrap/popover';
import {
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import template from './line-item-list-filters.component.html';
import {applyNetworkCalendarWeekStart, toBsDateInputFormat} from '../common/directives/form-controls/bs-datepicker-locale';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {getIntlLocaleData} from '../common/locale/intl-locale-data';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';

const DATE_FILTER_POPOVER_INSIDE_SELECTOR = [
  '.adn-line-item-date-filter-popover',
  'bs-datepicker-container',
  '.bs-datepicker'
].join(', ');

@Component({
  standalone: false,
  selector: 'adn-line-item-list-filters',
  template
})
export class LineItemListFiltersComponent implements OnInit, OnDestroy {
  @Input() list: any;

  readonly userStateOptions = ['PROPOSED', 'SUBMITTED', 'RESERVED', 'APPROVED', 'PAUSED', 'STOPPED'];
  readonly executionStateOptions = ['NOT_READY', 'READY', 'RUNNING', 'ENDED'];
  readonly sourceOptions = ['DIRECT', 'MARKETPLACE', 'SELF_SERVICE'];

  bsConfig: Partial<BsDatepickerConfig>;

  @ViewChild('dateFilterPopoverTrigger', {read: ElementRef}) dateFilterPopoverTrigger: ElementRef<HTMLElement>;
  @ViewChild('dateFilterPopoverTrigger', {read: PopoverDirective}) dateFilterPopoverDir: PopoverDirective;
  @ViewChild('orderFilterPopoverTrigger', {read: PopoverDirective}) orderFilterPopoverDir: PopoverDirective;
  @ViewChild('teamFilterPopoverTrigger', {read: PopoverDirective}) teamFilterPopoverDir: PopoverDirective;
  @ViewChild('targetingFilterPopoverTrigger', {read: PopoverDirective}) targetingFilterPopoverDir: PopoverDirective;
  @ViewChild('countryFilterPopoverTrigger', {read: PopoverDirective}) countryFilterPopoverDir: PopoverDirective;

  private dateFilterPopoverOutsideUnlisten: (() => void) | null = null;

  constructor(
    private readonly appLocale: AppLocaleService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly bsLocaleService: BsLocaleService,
    private readonly translate: TranslateService,
    private readonly renderer: Renderer2
  ) {
    this.bsConfig = this.createBsConfig();
  }

  ngOnInit(): void {
    this.applyDatePickerLocale();
  }

  ngOnDestroy(): void {
    this.unbindDateFilterPopoverOutsideClick();
  }

  onDateFilterPopoverShown(): void {
    this.applyDatePickerLocale();
    this.bindDateFilterPopoverOutsideClick();
  }

  onDateFilterPopoverHidden(): void {
    this.unbindDateFilterPopoverOutsideClick();
  }

  dateFilterActive(): boolean {
    return !!(this.list?.startDateFilter?.startDate || this.list?.endDateFilter?.endDate);
  }

  applyDateFilter(): void {
    this.list?.makeDateQuery?.();
    this.dateFilterPopoverDir?.hide();
  }

  clearDateFilter(): void {
    this.list?.makeDateQuery?.(true);
  }

  clearStartDateWorking(): void {
    if (this.list?.startDateFilter) {
      this.list.startDateFilter.workingStartDate = null;
    }
  }

  clearEndDateWorking(): void {
    if (this.list?.endDateFilter) {
      this.list.endDateFilter.workingEndDate = null;
    }
  }

  loadOrders(): void {
    this.list?.getOrders?.();
  }

  loadTeams(): void {
    this.list?.getTeams?.();
  }

  applyOrderFilter(): void {
    this.list?.makeOrderQuery?.();
    this.orderFilterPopoverDir?.hide();
  }

  clearOrderFilter(): void {
    this.list?.makeOrderQuery?.(true);
  }

  applyTeamFilter(): void {
    this.list?.makeTeamQuery?.();
    this.teamFilterPopoverDir?.hide();
  }

  clearTeamFilter(): void {
    this.list?.makeTeamQuery?.(true);
  }

  applyTargetingFilter(): void {
    this.list?.makeTargetingQuery?.();
    this.targetingFilterPopoverDir?.hide();
  }

  clearTargetingFilter(): void {
    this.list?.makeTargetingQuery?.(true);
  }

  applyCountryFilter(): void {
    this.list?.makeCountryTeamQuery?.();
    this.countryFilterPopoverDir?.hide();
  }

  clearCountryFilter(): void {
    this.list?.makeCountryTeamQuery?.(true);
  }

  get targetingTypeOptions(): any[] {
    const types = this.list?.targetingTypes;
    if (!types) {
      return [];
    }
    return Object.keys(types).map((key) => types[key]);
  }

  targetingTypeTitle(): string {
    const targetType = this.list?.targetType;
    return targetType ? this.list?.targetingTypes?.[targetType]?.title : '';
  }

  private bindDateFilterPopoverOutsideClick(): void {
    this.unbindDateFilterPopoverOutsideClick();
    // Skip the click that opened the popover.
    setTimeout(() => {
      this.dateFilterPopoverOutsideUnlisten = this.renderer.listen('document', 'click', (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        if (!target) {
          return;
        }
        // closest() still walks detached calendar nodes after a day is chosen.
        if (target.closest(DATE_FILTER_POPOVER_INSIDE_SELECTOR)) {
          return;
        }
        if (this.dateFilterPopoverTrigger?.nativeElement?.contains(target)) {
          return;
        }
        this.dateFilterPopoverDir?.hide();
      });
    });
  }

  private unbindDateFilterPopoverOutsideClick(): void {
    if (this.dateFilterPopoverOutsideUnlisten) {
      this.dateFilterPopoverOutsideUnlisten();
      this.dateFilterPopoverOutsideUnlisten = null;
    }
  }

  private applyDatePickerLocale(): void {
    applyNetworkCalendarWeekStart(
      this.bsLocaleService,
      this.LocalNetworkProfile.getDefaults()?.startDayCalendar,
      getIntlLocaleData(this.appLocale.getLocaleId())
    );
    this.bsConfig = this.createBsConfig();
  }

  private createBsConfig(): Partial<BsDatepickerConfig> {
    return {
      dateInputFormat: toBsDateInputFormat(getIntlLocaleData(this.appLocale.getLocaleId()).DATETIME_FORMATS.shortDate),
      adaptivePosition: false,
      returnFocusToInput: false,
      selectFromOtherMonth: true,
      showTodayButton: true,
      showClearButton: true,
      todayPosition: 'left',
      clearPosition: 'right',
      todayButtonLabel: this.translate.instant('button.today'),
      clearButtonLabel: this.translate.instant('button.clear')
    };
  }
}
